import React, { useRef, useState, useMemo } from "react";
import { UploadCloud, Play, Pause, Download, Trash2, Search, MoreVertical, Music4 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { DEFAULT_COVER, formatBytes } from "@/lib/format";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function UploadZone() {
  const { addFiles, uploading, t, ACCEPTED } = useApp();
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div
      data-testid="upload-zone"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
        drag ? "border-[#ff3366] bg-[#ff3366]/10" : "border-white/15 bg-white/[0.03] hover:border-white/30"
      }`}
    >
      <input
        ref={inputRef}
        data-testid="upload-input"
        type="file"
        accept={ACCEPTED.join(",") + ",audio/*"}
        multiple
        className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />
      <div className="mx-auto mb-3 h-12 w-12 grid place-items-center rounded-full bg-gradient-to-br from-[#ff3366] to-[#a020f0]">
        <UploadCloud className="h-6 w-6 text-white" />
      </div>
      <p className="font-display font-semibold text-base">{uploading ? t("processing") + "…" : t("upload")}</p>
      <p className="text-sm text-white/50 mt-0.5">{t("uploadHint")}</p>
      <p className="text-[11px] text-white/35 mt-2 tracking-wide">{t("uploadFormats")}</p>
    </div>
  );
}

function SongRow({ song, onDelete }) {
  const { currentId, isPlaying, loadAndPlay, togglePlay, downloadSong, t } = useApp();
  const active = currentId === song.id;
  return (
    <div
      data-testid={`song-row-${song.id}`}
      className={`group flex items-center gap-3 rounded-xl p-2 transition-colors ${
        active ? "bg-white/10" : "hover:bg-white/5"
      }`}
    >
      <button
        data-testid={`song-play-${song.id}`}
        onClick={() => (active ? togglePlay() : loadAndPlay(song.id))}
        className="relative h-12 w-12 shrink-0 rounded-lg overflow-hidden shadow-lg shadow-black/40"
      >
        <img src={song.cover || DEFAULT_COVER} alt="" className="h-full w-full object-cover" />
        <span className="absolute inset-0 grid place-items-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
          {active && isPlaying ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white" />}
        </span>
        {active && isPlaying && (
          <span className="absolute inset-0 grid place-items-center bg-black/40">
            <Pause className="h-5 w-5 text-[#ff3366]" fill="#ff3366" />
          </span>
        )}
      </button>
      <div className="min-w-0 flex-1" onClick={() => loadAndPlay(song.id)}>
        <p className={`truncate font-medium ${active ? "text-[#ff3366]" : "text-white"}`}>{song.title}</p>
        <p className="truncate text-sm text-white/50">{song.artist}</p>
      </div>
      <span className="hidden sm:inline text-[10px] text-white/30 mr-1">{formatBytes(song.size)}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`song-menu-${song.id}`} aria-label="menu" className="h-9 w-9 grid place-items-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors">
            <MoreVertical className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#121212] border-white/10 text-white">
          <DropdownMenuItem data-testid={`song-download-${song.id}`} onClick={() => downloadSong(song)} className="gap-2 focus:bg-white/10 cursor-pointer">
            <Download className="h-4 w-4" /> {t("download")}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`song-delete-${song.id}`} onClick={() => onDelete(song)} className="gap-2 text-red-400 focus:bg-white/10 focus:text-red-400 cursor-pointer">
            <Trash2 className="h-4 w-4" /> {t("delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function Library() {
  const { songs, removeSong, t } = useApp();
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return songs;
    return songs.filter((s) => s.title.toLowerCase().includes(q) || s.artist.toLowerCase().includes(q));
  }, [songs, query]);

  return (
    <div className="pt-4 space-y-4 fade-up">
      <UploadZone />

      {songs.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input
            data-testid="library-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search")}
            className="w-full rounded-full bg-white/[0.06] border border-white/10 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-[#ff3366]/60 transition-colors placeholder:text-white/40"
          />
        </div>
      )}

      {songs.length === 0 ? (
        <div data-testid="library-empty" className="text-center py-16">
          <div className="mx-auto mb-4 h-16 w-16 grid place-items-center rounded-2xl bg-white/5">
            <Music4 className="h-8 w-8 text-white/40" />
          </div>
          <p className="font-display font-semibold text-lg">{t("empty")}</p>
          <p className="text-sm text-white/50 mt-1">{t("emptyHint")}</p>
        </div>
      ) : (
        <div data-testid="song-list" className="space-y-1">
          {filtered.map((s) => (
            <SongRow key={s.id} song={s} onDelete={setPending} />
          ))}
        </div>
      )}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">{t("deleteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 border-0 text-white hover:bg-white/20">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={() => { removeSong(pending.id); setPending(null); }}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t("remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
